import React from 'react';
import { hydrate } from 'react-dom';
import { Provider } from 'react-redux';
import { loadableReady } from '@loadable/component';
import { BrowserRouter as Router, Route, Switch } from 'react-router-dom';
import { Frontload } from 'react-frontload';

import { logger } from 'logging';
import { initLogger } from '@nykaa/nes-logger';
import { configureStore } from 'shared/store';
import App from 'shared/App';
import Nps from 'shared/Nps';
import User from 'User';

import polyfillPromise from 'client/polyfills';
import { addRequestInterceptor } from 'api';
import { csrfHeader } from 'api/interceptor/csrf';
import { storeParam } from 'api/interceptor/store';
import { addRequestHeaders, isExcludedUrl } from 'api/interceptor/headers';
import { catalogTagFilter } from 'api/interceptor/catalogTagFilter';
import { npsStore } from 'Nps/store';
import { configureUserStore } from 'User/store';
import { VP } from 'api/interceptor/visitorPrioritization';
import { getLogDir } from 'server/helpers';
import { listenCloudStore } from 'client/service/fireStore';
import { changeData } from 'shared/store/visitorPrioritization/action';

const {
  __PRELOADED_STATE__: initialState,
  __NPS_PRELOADED_STATE__: npsInitialState,
  __USER_PRELOADED_STATE__: userInitialState,
} = window;

const store = window.store || configureStore({ initialState });
const storeData = window.store || npsStore({ initialState: npsInitialState });
const userStore =
  window.store || configureUserStore({ initialState: userInitialState });

// Initialize VP Firestore listener immediately if enabled
const { vpEnabled } = initialState.remoteConfig.switches;
const vpConfig = initialState.remoteConfig.configs.VP;

if (vpEnabled && vpConfig) {
  listenCloudStore(vpConfig, async (data) => {
    store.dispatch(changeData(data));
  });
}

const { sentrySampleRate, newRelicSampleRate, apiRequestHeaders } =
  initialState.remoteConfig.configs;
const sentryOptions = { sampleRate: sentrySampleRate };
const newRelicOptions = { sampleRate: newRelicSampleRate };
initLogger(getLogDir(), sentryOptions, newRelicOptions);
addRequestInterceptor(csrfHeader);
addRequestInterceptor(storeParam);
addRequestInterceptor(catalogTagFilter);
addRequestInterceptor((config) => VP(config, store));
addRequestInterceptor((config) => addRequestHeaders(config, apiRequestHeaders));

// Override X-NES-Language header with actual language from Redux store (CSR)
// Skip excluded URLs (same as SSR exclusions)
addRequestInterceptor((config) => {
  if (isExcludedUrl(config.url || '')) {
    return config;
  }
  const state = store.getState();
  const lang = state.langConfig?.lang || 'en';
  if (config.headers) {
    config.headers['X-NES-Language'] = lang;
  }
  return config;
});

polyfillPromise.then(() => {
  loadableReady(() => {
    hydrate(
      <Router>
        <Switch>
          <Route path="/nps/review">
            {/**
             * FIXME:  Move this to inside the NPS sub app
             * and pass the store to Nps as
             * <Nps store={storeData} />
             */}
            <Provider store={storeData}>
              <Frontload>
                <Nps />
              </Frontload>
            </Provider>
          </Route>
          <Route path="/user">
            <User store={userStore} />
          </Route>
          <Route path="*">
            <Provider store={store}>
              <Frontload>
                <App />
              </Frontload>
            </Provider>
          </Route>
        </Switch>
      </Router>,
      document.getElementById('app')
    );
  });
});

if (process.env.NODE_ENV === 'development') {
  if (module.hot) {
    module.hot.accept();
  }

  if (!window.store) {
    window.store = store;
  }
}

if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    const cid = process.env.GAMOOGA_TRACKING_ID;

    navigator.serviceWorker
      .register(`/sw.js?cid=${cid}`)
      .then(() => {
        // The above then resolves with "registration" object
        // but we chose to ignore it for the moment
      })
      .catch((registrationError) => {
        logger.error(registrationError, 'Error installing service worker file');
      });
  });

  const pwaPromptListener = (promptEvent: Event) => {
    promptEvent.preventDefault();
  };

  window.addEventListener('beforeinstallprompt', pwaPromptListener);
}
